import { DraftAssignmentPreview, LearningPathPicker } from './TrainingAdminWorkspace';
// components/hr/TrainingAssignmentModal.tsx
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Button,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormHelperText,
} from '@mui/joy';
import {
  Plus as AddOutlined,
  Trash as DeleteOutlined,
  PlayCircle as PlayCircleOutlined,
  Play as PlayOutlined,
  FilePdf as PictureAsPdfOutlined,
  LinkSimple as LinkOutlined,
  ListChecks as ChecklistOutlined,
  Question as QuizOutlined,
  Article as ArticleOutlined,
  CheckCircle as CheckCircleOutlined,
  Clock as AccessTimeOutlined,
  X as CloseOutlined,
} from '@phosphor-icons/react';
import {
  useTrainingLibrary,
  useTrainingAssignment,
  useCreateTrainingAssignment,
  useUpdateTrainingAssignment,
  useHrSettings,
} from '../../hooks/useHr';
import { apiClient } from '../../lib/api';
import type {
  CreateTrainingAssignmentRequest,
  AssignmentCriteria,
  TeamMemberListItem,
  TrainingContentType,
} from '../../types/hr';

interface TrainingAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId?: string | null;
  onSuccess?: () => void;
}

/** The shape TrainingAssignment.resolve_members() reads for a 'custom' rule. */
interface CustomCriteria {
  departments?: string[];
  job_titles?: string[];
  team_member_ids?: string[];
}

/** Apply the rule now. Idempotent — re-running never duplicates enrollments. */
const useRunAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; created_count: number }>(
        `/api/training/assignments/${id}/run/`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-progress'] });
      queryClient.invalidateQueries({ queryKey: ['training-progress-summary'] });
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });
};

/**
 * Every team member, for the custom-selection picker. The shared useTeamMembers
 * hook pages at 20, which silently hides most of a seasonal crew.
 */
const useAllTeamMembers = (enabled: boolean) =>
  useQuery({
    queryKey: ['team-members', 'all'],
    queryFn: async () => {
      const response = await apiClient.get<{ results: TeamMemberListItem[] } | TeamMemberListItem[]>(
        '/api/hr/team-members/?page_size=1000'
      );
      return Array.isArray(response.data) ? response.data : response.data.results;
    },
    enabled,
  });

/** Free-text chip list — departments and job titles are typed, not enumerated. */
const TokenListInput: React.FC<{
  label: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
}> = ({ label, placeholder, values, onChange, suggestions = [] }) => {
  const [draft, setDraft] = useState('');

  const add = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed]);
    setDraft('');
  };

  const unused = suggestions.filter((s) => s && !values.includes(s)).slice(0, 8);

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      {values.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {values.map((value) => (
            <Chip
              key={value}
              size="sm"
              variant="soft"
              endDecorator={
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => onChange(values.filter((v) => v !== value))}
                >
                  <CloseOutlined size={14} />
                </IconButton>
              }
            >
              {value}
            </Chip>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Input
          size="sm"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(draft);
            }
          }}
          sx={{ flex: 1 }}
        />
        <IconButton size="sm" onClick={() => add(draft)}>
          <AddOutlined />
        </IconButton>
      </Box>
      {unused.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {unused.map((suggestion) => (
            <Chip
              key={suggestion}
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => add(suggestion)}
            >
              + {suggestion}
            </Chip>
          ))}
        </Box>
      )}
    </FormControl>
  );
};

const contentTypeIcons: Record<TrainingContentType, React.ReactNode> = {
  video: <PlayCircleOutlined />,
  pdf: <PictureAsPdfOutlined />,
  link: <LinkOutlined />,
  checklist: <ChecklistOutlined />,
  quiz: <QuizOutlined />,
  article: <ArticleOutlined />,
  scorm: <PlayCircleOutlined />,
  presentation: <PictureAsPdfOutlined />,
};

const criteriaLabels: Record<AssignmentCriteria, string> = {
  all: 'All Team Members',
  w2: 'W-2 Employees Only',
  '1099': '1099 Contractors Only',
  custom: 'Custom Selection',
};

// What a new assignment starts from until the org's own training defaults
// arrive. Named so the late-arrival effect can tell a value it put there from
// one the user typed while the settings were still loading.
const FALLBACK_DUE_DAYS = 30;
const FALLBACK_SEND_REMINDERS = true;
const FALLBACK_REMINDER_DAYS = [7, 3, 1];

const isFallbackReminderDays = (days: number[] | undefined): boolean =>
  (days ?? []).join(',') === FALLBACK_REMINDER_DAYS.join(',');

export const TrainingAssignmentModal: React.FC<TrainingAssignmentModalProps> = ({
  open,
  onClose,
  assignmentId,
  onSuccess,
}) => {
  const isEditing = !!assignmentId;

  const { data: existingAssignment, isLoading: loadingAssignment } = useTrainingAssignment(
    assignmentId || ''
  );
  const { data: trainingItems = [], isLoading: loadingItems, error: itemsError } = useTrainingLibrary({ is_active: true });
  const { data: hrSettings } = useHrSettings();
  const createAssignment = useCreateTrainingAssignment();
  const updateAssignment = useUpdateTrainingAssignment();

  const [error, setError] = useState<string>('');
  const [runMessage, setRunMessage] = useState<string>('');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [itemPage, setItemPage] = useState(1);
  const [memberSearch, setMemberSearch] = useState('');

  // Team → Settings already holds the org's answer to all three of these, and
  // the enrolment service honours the due-days value, so a new assignment
  // should start there rather than at a hardcoded 30/7-3-1.
  const createDefaults = (): CreateTrainingAssignmentRequest => ({
    name: '',
    description: '',
    training_item_ids: [],
    criteria: 'all',
    is_onboarding: false,
    due_days_after_start: hrSettings?.default_training_due_days ?? FALLBACK_DUE_DAYS,
    is_active: true,
    is_mandatory: true,
    send_reminders: hrSettings?.send_training_reminders ?? FALLBACK_SEND_REMINDERS,
    // Copied, not aliased: the form replaces this array wholesale on edit, but
    // a shared reference in state is one careless splice from rewriting the
    // fallback (or the cached settings) for every other form on the page.
    reminder_days_before_due: [...(hrSettings?.training_reminder_days ?? FALLBACK_REMINDER_DAYS)],
  });

  const [formData, setFormData] = useState<CreateTrainingAssignmentRequest>(createDefaults);
  // Whether the org's training defaults have been folded into this form yet.
  const settingsSeededRef = useRef(false);

  // Load existing assignment data for editing
  useEffect(() => {
    if (isEditing && existingAssignment) {
      setFormData({
        name: existingAssignment.name,
        description: existingAssignment.description,
        training_item_ids: existingAssignment.training_items,
        criteria: existingAssignment.criteria,
        custom_criteria: existingAssignment.custom_criteria,
        is_onboarding: existingAssignment.is_onboarding,
        due_days_after_start: existingAssignment.due_days_after_start,
        is_active: existingAssignment.is_active,
        is_mandatory: existingAssignment.is_mandatory,
        send_reminders: existingAssignment.send_reminders,
        reminder_days_before_due: existingAssignment.reminder_days_before_due,
      });
    }
  }, [isEditing, existingAssignment]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open && !isEditing) {
      setFormData(createDefaults());
      settingsSeededRef.current = !!hrSettings;
      setError('');
      setShowItemSelector(false);
    }
    if (open) {
      setRunMessage('');
      setMemberSearch('');
    }
  }, [open, isEditing]);

  // The modal can be opened before the settings query resolves. Apply the org's
  // defaults when they land, exactly once - a later refetch must never reset a
  // form somebody is filling in. Each field is replaced only while it still
  // holds the fallback: anything else in there was typed in the meantime and
  // belongs to the user.
  useEffect(() => {
    if (!open || isEditing || !hrSettings || settingsSeededRef.current) return;
    settingsSeededRef.current = true;
    setFormData(prev => ({
      ...prev,
      due_days_after_start:
        prev.due_days_after_start === FALLBACK_DUE_DAYS
          ? hrSettings.default_training_due_days
          : prev.due_days_after_start,
      send_reminders:
        prev.send_reminders === FALLBACK_SEND_REMINDERS
          ? hrSettings.send_training_reminders
          : prev.send_reminders,
      reminder_days_before_due: isFallbackReminderDays(prev.reminder_days_before_due)
        ? hrSettings.training_reminder_days
        : prev.reminder_days_before_due,
    }));
  }, [open, isEditing, hrSettings]);

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      setError('Assignment name is required');
      return;
    }
    if (!formData.training_item_ids || formData.training_item_ids.length === 0) {
      setError('Please select at least one training item');
      return;
    }
    // resolve_members() returns nobody for an empty custom rule, so this would
    // otherwise save a rule that quietly enrolls no one.
    if (formData.criteria === 'custom' && !hasCustomSelection) {
      setError('Pick at least one department, job title or team member for a custom rule');
      return;
    }

    try {
      if (isEditing && assignmentId) {
        await updateAssignment.mutateAsync({ id: assignmentId, data: formData });
      } else {
        await createAssignment.mutateAsync(formData);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'create'} assignment. Please try again.`);
    }
  };

  const handleAddItem = (itemId: string) => {
    const current = formData.training_item_ids || [];
    if (!current.includes(itemId)) {
      setFormData({ ...formData, training_item_ids: [...current, itemId] });
    }
  };

  const handleRemoveItem = (itemId: string) => {
    const current = formData.training_item_ids || [];
    setFormData({ ...formData, training_item_ids: current.filter((id) => id !== itemId) });
  };

  const selectedItems = trainingItems.filter((item) =>
    formData.training_item_ids?.includes(item.id)
  );
  const availableItems = trainingItems.filter(
    (item) => !formData.training_item_ids?.includes(item.id) && item.title.toLowerCase().includes(itemSearch.toLowerCase())
  );

  // ---- Custom selection -----------------------------------------------------
  const isCustom = formData.criteria === 'custom';
  const custom = (formData.custom_criteria || {}) as CustomCriteria;
  const setCustom = (patch: Partial<CustomCriteria>) =>
    setFormData((prev) => ({
      ...prev,
      custom_criteria: { ...((prev.custom_criteria || {}) as CustomCriteria), ...patch },
    }));

  const { data: allMembers = [] } = useAllTeamMembers(open && isCustom);
  const selectedMemberIds = custom.team_member_ids || [];

  const memberMatches = useMemo(() => {
    const needle = memberSearch.trim().toLowerCase();
    if (!needle) return allMembers;
    return allMembers.filter(
      (member) =>
        member.full_name.toLowerCase().includes(needle) ||
        (member.job_title || '').toLowerCase().includes(needle) ||
        (member.department || '').toLowerCase().includes(needle)
    );
  }, [allMembers, memberSearch]);

  const departmentSuggestions = useMemo(
    () => Array.from(new Set(allMembers.map((m) => m.department).filter(Boolean))),
    [allMembers]
  );
  const jobTitleSuggestions = useMemo(
    () => Array.from(new Set(allMembers.map((m) => m.job_title).filter(Boolean))),
    [allMembers]
  );

  const hasCustomSelection =
    (custom.departments?.length || 0) +
      (custom.job_titles?.length || 0) +
      selectedMemberIds.length >
    0;

  // ---- Blast radius / run ---------------------------------------------------

  const runAssignment = useRunAssignment();

  const handleRun = async () => {
    if (!assignmentId) return;
    try {
      const result = await runAssignment.mutateAsync(assignmentId);
      setRunMessage(result.message);
      setError('');
    } catch {
      setError('Failed to apply this assignment. Please try again.');
    }
  };

  const isLoading = loadingAssignment || loadingItems;
  const isPending = createAssignment.isPending || updateAssignment.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 650,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <ModalClose />
        <DialogTitle>{isEditing ? 'Edit Assignment' : 'Create Training Assignment'}</DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Basic Info */}
                <FormControl required>
                  <FormLabel>Assignment Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., New Hire Safety Training"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this assignment covers"
                    minRows={2}
                  />
                </FormControl>

                <Divider />

                {/* Training Items Selection */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography level="title-sm">Training Items *</Typography>
                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<AddOutlined />}
                      onClick={() => setShowItemSelector(!showItemSelector)}
                    >
                      {showItemSelector ? 'Hide Items' : 'Add Items'}
                    </Button>
                  </Box>

                  {/* Selected Items */}
                  {selectedItems.length > 0 ? (
                    <List sx={{ mb: 2 }}>
                      {selectedItems.map((item, index) => (
                        <ListItem
                          key={item.id}
                          endAction={
                            <IconButton
                              size="sm"
                              color="danger"
                              variant="plain"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <DeleteOutlined />
                            </IconButton>
                          }
                        >
                          <ListItemDecorator>
                            <Typography level="body-sm" color="neutral">
                              {index + 1}.
                            </Typography>
                          </ListItemDecorator>
                          <ListItemContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {contentTypeIcons[item.content_type]}
                              <Typography level="body-sm">{item.title}</Typography>
                              {item.estimated_duration_minutes > 0 && (
                                <Chip size="sm" variant="plain" startDecorator={<AccessTimeOutlined />}>
                                  {item.estimated_duration_minutes} min
                                </Chip>
                              )}
                            </Box>
                          </ListItemContent>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                      No training items selected. Click "Add Items" to select training content.
                    </Typography>
                  )}

                  {/* Item Selector */}
                  {showItemSelector && (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 'sm',
                        maxHeight: 200,
                        overflow: 'auto',
                        p: 1,
                      }}
                    >
                      <Input placeholder="Search all courses" value={itemSearch} onChange={e => { setItemSearch(e.target.value); setItemPage(1); }} />
                      {itemsError && <Alert color="danger">Could not load the course catalog.</Alert>}
                      <Button size="sm" disabled={itemPage <= 1} onClick={() => setItemPage(itemPage - 1)}>Previous</Button><Button size="sm" disabled={itemPage * 20 >= availableItems.length} onClick={() => setItemPage(itemPage + 1)}>Next</Button>
                      {availableItems.length === 0 ? (
                        <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 2 }}>
                          {trainingItems.length === 0
                            ? 'No training items available. Create some in the Library tab first.'
                            : 'All training items have been added.'}
                        </Typography>
                      ) : (
                        availableItems.slice((itemPage - 1) * 20, itemPage * 20).map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 'sm',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'neutral.100' },
                            }}
                            onClick={() => handleAddItem(item.id)}
                          >
                            <Checkbox size="sm" checked={false} readOnly />
                            {contentTypeIcons[item.content_type]}
                            <Box sx={{ flex: 1 }}>
                              <Typography level="body-sm">{item.title}</Typography>
                              {item.category && (
                                <Typography level="body-xs" color="neutral">
                                  {item.category}
                                </Typography>
                              )}
                            </Box>
                            {item.estimated_duration_minutes > 0 && (
                              <Typography level="body-xs" color="neutral">
                                {item.estimated_duration_minutes} min
                              </Typography>
                            )}
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Assignment Criteria */}
                <FormControl>
                  <FormLabel>Assign To</FormLabel>
                  <Select
                    value={formData.criteria}
                    onChange={(_, value) =>
                      setFormData({ ...formData, criteria: value as AssignmentCriteria })
                    }
                  >
                    {Object.entries(criteriaLabels).map(([value, label]) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formData.criteria === 'all' && 'This training will be assigned to all team members'}
                    {formData.criteria === 'w2' && 'Only W-2 employees will receive this training'}
                    {formData.criteria === '1099' && 'Only 1099 contractors will receive this training'}
                    {formData.criteria === 'custom' &&
                      'Anyone matching any department, job title or name you pick below'}
                  </FormHelperText>
                  <FormHelperText>
                    Terminated and inactive team members are always excluded.
                  </FormHelperText>
                </FormControl>

                {/* Custom selection — departments, job titles and named people.
                    The backend ORs these together; an empty custom rule covers
                    nobody rather than everybody. */}
                {isCustom && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      pl: 2,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <TokenListInput
                      label="Departments"
                      placeholder="e.g. Field Crew"
                      values={custom.departments || []}
                      onChange={(next) => setCustom({ departments: next })}
                      suggestions={departmentSuggestions}
                    />
                    <TokenListInput
                      label="Job Titles"
                      placeholder="e.g. Roofer"
                      values={custom.job_titles || []}
                      onChange={(next) => setCustom({ job_titles: next })}
                      suggestions={jobTitleSuggestions}
                    />

                    <FormControl>
                      <FormLabel>Specific Team Members</FormLabel>
                      <Input
                        size="sm"
                        placeholder="Search team members..."
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 'sm',
                          maxHeight: 200,
                          overflow: 'auto',
                          p: 1,
                        }}
                      >
                        {memberMatches.length === 0 ? (
                          <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 2 }}>
                            No team members match.
                          </Typography>
                        ) : (
                          memberMatches.map((member) => (
                            <Box
                              key={member.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 0.5,
                                borderRadius: 'sm',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'neutral.100' },
                              }}
                              onClick={() =>
                                setCustom({
                                  team_member_ids: selectedMemberIds.includes(member.id)
                                    ? selectedMemberIds.filter((id) => id !== member.id)
                                    : [...selectedMemberIds, member.id],
                                })
                              }
                            >
                              <Checkbox
                                size="sm"
                                checked={selectedMemberIds.includes(member.id)}
                                readOnly
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography level="body-sm">{member.full_name}</Typography>
                                <Typography level="body-xs" color="neutral">
                                  {[member.job_title, member.department].filter(Boolean).join(' · ') ||
                                    member.member_type}
                                </Typography>
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                      <FormHelperText>
                        {selectedMemberIds.length} selected
                      </FormHelperText>
                    </FormControl>

                    {!hasCustomSelection && (
                      <Alert size="sm" color="warning">
                        A custom rule with nothing selected covers nobody.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Due Days */}
                <FormControl>
                  <FormLabel>Due Days After Start</FormLabel>
                  <Input
                    type="number"
                    value={formData.due_days_after_start}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        due_days_after_start: parseInt(e.target.value) || 0,
                      })
                    }
                    slotProps={{ input: { min: 1, max: 365 } }}
                  />
                  <FormHelperText>
                    Number of days after start date that training is due
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Options */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-sm">Onboarding Training</Typography>
                      <Typography level="body-xs" color="neutral">
                        Auto-assign to new hires during onboarding
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_onboarding}
                      onChange={(e) => setFormData({ ...formData, is_onboarding: e.target.checked })}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-sm">Mandatory</Typography>
                      <Typography level="body-xs" color="neutral">
                        Team members must complete this training
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-sm">Send Reminders</Typography>
                      <Typography level="body-xs" color="neutral">
                        Email the assignee before the due date
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.send_reminders}
                      onChange={(e) => setFormData({ ...formData, send_reminders: e.target.checked })}
                    />
                  </Box>

                  {formData.send_reminders && (
                    <FormControl sx={{ maxWidth: 220 }}>
                      <FormLabel>Remind This Many Days Before</FormLabel>
                      <Input
                        size="sm"
                        value={(formData.reminder_days_before_due ?? []).join(', ')}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            reminder_days_before_due: e.target.value
                              .split(',')
                              .map((day) => parseInt(day.trim(), 10))
                              .filter((day) => !Number.isNaN(day) && day > 0),
                          })
                        }
                        placeholder="7, 3, 1"
                      />
                      <FormHelperText>
                        Comma-separated. Leave blank to use the org default from HR Settings.
                      </FormHelperText>
                    </FormControl>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography level="body-sm">Active</Typography>
                      <Typography level="body-xs" color="neutral">
                        Assignment is currently active
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  </Box>
                </Box>

                <LearningPathPicker onSelect={ids => setFormData({ ...formData, training_item_ids: Array.from(new Set([...(formData.training_item_ids || []), ...ids])) })} />
                <DraftAssignmentPreview draft={formData} assignmentId={assignmentId} />
                {isEditing && <Box><Button startDecorator={<PlayOutlined />} loading={runAssignment.isPending} onClick={handleRun}>Enroll using saved rule</Button><Typography level="body-xs">Save your draft first to enroll using changed criteria.</Typography>{runMessage && <Alert color="success">{runMessage}</Alert>}</Box>}

              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isPending}
            startDecorator={<CheckCircleOutlined />}
            disabled={isLoading}
          >
            {isEditing ? 'Save Changes' : 'Create Assignment'}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default TrainingAssignmentModal;
